import { Alert, Box, Typography } from '@mui/material';
import { isMobile } from 'react-device-detect';
import { ISpec, ISpecCategory, ISpecOption } from '../../../../models';
import { AddOption } from '../AddSpecOption';
import { MobileCategoryOptions } from '../MobileCategoryOptions';
import { Table } from '../../../../components';
import { SALES_PORTAL_PER_PAGE_OPTIONS, getEnvironment } from '../../../../helpers';
import { useContext, useMemo } from 'react';
import { SalesContext, update } from '../../../../context/sales';

interface IContractOptions {
  isFetchingOptionsCategories: boolean;
  hasCorrectRole: () => boolean;
  specOptionsCategories: ISpecCategory[];
  editableContractCategoryOptions: ISpecOption[];
  setEditableContractCategoryOptions: (options: ISpecOption[]) => void;
  setOverrideExistingSelection: (val: boolean) => void;
  overrideExistingSelection: boolean;
  spec: ISpec;
  getColumns: (isContract?: boolean) => any[];
  editingId: string;
  setEditingId: (val: string) => void;
  isFetchingOptions: boolean;
  updateOptionsData: (
    option: ISpecOption,
    columnId: string,
    value: string,
    error?: boolean,
    isContract?: boolean
  ) => void;
  isPrint?: boolean;
}

const PAGE_NAME = 'Contract-Options';
export const ContractOptions: React.FC<IContractOptions> = ({
  isFetchingOptionsCategories,
  hasCorrectRole,
  specOptionsCategories,
  editableContractCategoryOptions,
  setEditableContractCategoryOptions,
  setOverrideExistingSelection,
  overrideExistingSelection,
  spec,
  getColumns,
  editingId,
  setEditingId,
  isFetchingOptions,
  updateOptionsData,
  isPrint
}) => {
  const { perPageSalesPortalValues, dispatchPerPage } = useContext(SalesContext);

  const currentPageState = perPageSalesPortalValues.find(
    state => state?.pageName === PAGE_NAME
  )?.itemsPerPage;
  const perPage = useMemo<number>(
    () =>
      currentPageState && currentPageState >= SALES_PORTAL_PER_PAGE_OPTIONS[0]
        ? currentPageState
        : SALES_PORTAL_PER_PAGE_OPTIONS[0],
    [currentPageState]
  );

  const setRowsPerPage = (itemsPerPage: number) => {
    return dispatchPerPage(
      update({
        pageName: PAGE_NAME,
        environment: getEnvironment(),
        itemsPerPage: itemsPerPage
      })
    );
  };
  return (
    <div key={'contract-options-tab-content'}>
      <Box displayPrint="block">
        <Typography variant="h6" gutterBottom paddingTop="1rem">
          Contract Options
        </Typography>
      </Box>
      {!isFetchingOptionsCategories && hasCorrectRole() && (
        <AddOption
          categories={specOptionsCategories.filter(cat => cat.displayName.includes('Contract'))}
          options={editableContractCategoryOptions}
          setOptions={setEditableContractCategoryOptions}
          setOverride={setOverrideExistingSelection}
          override={overrideExistingSelection}
        />
      )}
      {spec.missingContractCategories === null && (
        <Alert severity="success">Contract Selections Complete</Alert>
      )}
      {spec.missingContractCategories !== null && (
        <Alert severity="error">Contract Selections Incomplete</Alert>
      )}
      <Table
        columns={getColumns(true).filter(col => {
          if (!hasCorrectRole()) {
            return col.id !== 'actions';
          }
          return true;
        })}
        data={editableContractCategoryOptions}
        isLoading={isFetchingOptions}
        useTableProps={{
          updateMyData: (option: ISpecOption, columnId: string, value: string, error?: boolean) =>
            updateOptionsData(option, columnId, value, error, true)
        }}
        hidePagination={isPrint}
        mobileProps={{
          options: editableContractCategoryOptions,
          setOptions: setEditableContractCategoryOptions,
          editingId: editingId,
          handleEdit: (val: ISpecOption) => {
            const isEditing = editingId === val.optionId;
            if (isEditing) {
              setEditingId(null);
            } else {
              setEditingId(val.optionId);
            }
          },
          handleDelete: (val: ISpecOption) => {
            const result = window.confirm('Are you sure you want to remove this item?');
            if (result) {
              setEditableContractCategoryOptions(
                editableContractCategoryOptions.filter(option => val.optionId !== option.optionId)
              );
            } else {
              return;
            }
          }
        }}
        rowsPerPageOptions={SALES_PORTAL_PER_PAGE_OPTIONS}
        rowsPerPage={perPage}
        setRowsPerPage={setRowsPerPage}
        ResponsiveComponent={isMobile ? MobileCategoryOptions : undefined}
        scrollToTopOnPaging
      />
    </div>
  );
};
